import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';
import { uspCardApi } from '../../services/api';
import type { UspCard } from '../../models';

const UspCardList: React.FC = () => {
  const navigate = useNavigate();
  const [uspCards, setUspCards] = useState<UspCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchUspCards();
  }, []);

  const fetchUspCards = async () => {
    try {
      setLoading(true);
      const response = await uspCardApi.list();
      setUspCards(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch USP cards');
      console.error('Error fetching USP cards:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this USP card?')) {
      try {
        await uspCardApi.delete(id);
        fetchUspCards();
      } catch (err) {
        console.error('Error deleting USP card:', err);
      }
    }
  };

  const handleReorder = async (id: number, direction: 'up' | 'down') => {
    const card = uspCards.find(c => c.id === id);
    if (!card) return;

    const currentIndex = uspCards.findIndex(c => c.id === id);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= uspCards.length) return;

    const reorderedCards = [...uspCards];
    const [movedCard] = reorderedCards.splice(currentIndex, 1);
    reorderedCards.splice(newIndex, 0, movedCard);

    const updatedOrder = reorderedCards.map((card, index) => ({
      id: card.id,
      order: index + 1,
    }));

    try {
      await uspCardApi.reorder(updatedOrder);
      setUspCards(reorderedCards);
    } catch (err) {
      console.error('Error reordering USP cards:', err);
    }
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">USP Cards</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/usp-cards/new')}
        >
          New USP Card
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Order</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Sub Title</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Link Type</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {uspCards.map((card) => (
              <TableRow key={card.id}>
                <TableCell>{card.order}</TableCell>
                <TableCell>{card.title}</TableCell>
                <TableCell>{card.sub_title}</TableCell>
                <TableCell>{card.description}</TableCell>
                <TableCell>
                  {card.link_type_app && (
                    <Chip
                      label={`App: ${card.link_type_app}`}
                      color="primary"
                      size="small"
                      sx={{ mr: 1 }}
                    />
                  )}
                  {card.link_type_web && (
                    <Chip
                      label={`Web: ${card.link_type_web}`}
                      color="secondary"
                      size="small"
                    />
                  )}
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/usp-cards/${card.id}`)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(card.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleReorder(card.id, 'up')}
                    disabled={card.order === 1}
                  >
                    <ArrowUpwardIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleReorder(card.id, 'down')}
                    disabled={card.order === uspCards.length}
                  >
                    <ArrowDownwardIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default UspCardList; 